/**
 * section_type → component: the one table of homepage sections the storefront
 * can render. `Home.tsx` and `SaasHome.tsx` look rows up here instead of
 * carrying their own switch statements (the SaaS copy had drifted: it
 * accepted six types it never rendered), and the site-templates registry test
 * reads `SECTION_TYPES` to prove every template recipe names a real section.
 *
 * Adding a section: add the component here. Also add it to the DB CHECK
 * (`homepage_sections_section_type_check`, a new migration) and to
 * `HomepageBuilder.tsx`'s label list so admins can create it.
 */
import type { ComponentType } from 'react';
import HeroSection from '@/components/homepage/HeroSection';
import FeaturedProducts from '@/components/homepage/FeaturedProducts';
import AppEmbedSection from '@/components/homepage/AppEmbedSection';
import CategoryGrid from '@/components/homepage/CategoryGrid';
import StorySection from '@/components/homepage/StorySection';
import ImageBanner from '@/components/homepage/ImageBanner';
import SeasonalSpotlight from '@/components/homepage/SeasonalSpotlight';
import ValuePropositions from '@/components/homepage/ValuePropositions';
import ImageGallery from '@/components/homepage/ImageGallery';
import Newsletter from '@/components/homepage/Newsletter';
import Testimonials from '@/components/homepage/Testimonials';
import Banner from '@/components/homepage/Banner';
import GrowingTips from '@/components/homepage/GrowingTips';
import CustomHtml from '@/components/homepage/CustomHtml';
import PricingTiers from '@/components/homepage/PricingTiers';
import CourseHighlight from '@/components/homepage/CourseHighlight';
import InstructorBios from '@/components/homepage/InstructorBios';
import FAQ from '@/components/homepage/FAQ';
import StatsCounter from '@/components/homepage/StatsCounter';
import TrustBadges from '@/components/homepage/TrustBadges';
import LearningPath from '@/components/homepage/LearningPath';
import VideoShowcase from '@/components/homepage/VideoShowcase';
import FeatureComparison from '@/components/homepage/FeatureComparison';
import CTABanner from '@/components/homepage/CTABanner';
import ProcessSteps from '@/components/homepage/ProcessSteps';
import {
  CurrentExhibitHero,
  VisitCard,
  EventsTeaser,
  MissionBrief,
  DonateCta,
  MembershipTiersSection,
} from '@/components/homepage/MuseumSections';
import {
  AvailabilitySearchHero,
  FeaturedProperties,
  HostIntro,
  AreaHighlights,
  ReviewsTeaser,
  DirectBookPitch,
} from '@/components/homepage/LodgingSections';
import {
  PlayNowHero,
  HowItWorksSteps,
  FeaturedGamesGrid,
  WinnersTeaser,
  ResponsiblePlayBadge,
  AppEmbedTeaser,
} from '@/components/homepage/SocialCasinoSections';

export type SectionComponent = ComponentType<{ section: any }>;

export const SECTION_COMPONENTS: Readonly<Record<string, SectionComponent>> = {
  // General
  hero: HeroSection,
  featured_products: FeaturedProducts,
  new_arrivals: FeaturedProducts,
  categories: CategoryGrid,
  story: StorySection,
  image_banner: ImageBanner,
  seasonal_spotlight: SeasonalSpotlight,
  value_props: ValuePropositions,
  image_gallery: ImageGallery,
  newsletter: Newsletter,
  testimonials: Testimonials,
  banner: Banner,
  growing_tips: GrowingTips,
  custom_html: CustomHtml,
  // SaaS / courses
  pricing_tiers: PricingTiers,
  course_highlight: CourseHighlight,
  instructor_bios: InstructorBios,
  faq: FAQ,
  stats_counter: StatsCounter,
  trust_badges: TrustBadges,
  learning_path: LearningPath,
  video_showcase: VideoShowcase,
  feature_comparison: FeatureComparison,
  cta_banner: CTABanner,
  process_steps: ProcessSteps,
  app_embed: AppEmbedSection,
  // Museum (live for siteType='museum'; any tenant may opt in via the builder)
  current_exhibit_hero: CurrentExhibitHero,
  visit_card: VisitCard,
  events_teaser: EventsTeaser,
  mission_brief: MissionBrief,
  donate_cta: DonateCta,
  membership_tiers: MembershipTiersSection,
  // Lodging
  availability_search: AvailabilitySearchHero,
  featured_properties: FeaturedProperties,
  host_intro: HostIntro,
  area_highlights: AreaHighlights,
  reviews_teaser: ReviewsTeaser,
  direct_book_pitch: DirectBookPitch,
  // Social casino
  play_now_hero: PlayNowHero,
  how_it_works_steps: HowItWorksSteps,
  featured_games_grid: FeaturedGamesGrid,
  winners_teaser: WinnersTeaser,
  responsible_play_badge: ResponsiblePlayBadge,
  app_embed_teaser: AppEmbedTeaser,
};

/** Every section_type the storefront can render. */
export const SECTION_TYPES: readonly string[] = Object.keys(SECTION_COMPONENTS);

export function getSectionComponent(sectionType: unknown): SectionComponent | undefined {
  return typeof sectionType === 'string' ? SECTION_COMPONENTS[sectionType] : undefined;
}
